/**
 * RWBR — AI narration, laid on top of the deterministic booth.
 *
 * D-027's precondition is met: `src/domain/booth/` is built, wired and gated by
 * `repetition.test.ts` against NARRATION_MODEL §8.7. This module is the layer
 * above it, and the ordering in the name matters — **the booth is the floor,
 * the model is the finish.** Nothing here can make the game quieter, slower or
 * less true than it already is:
 *
 *   • Every beat already HAS a line before this module is called. The model is
 *     offered the chance to write a better one and can decline by failing.
 *   • Every candidate goes through `verifyLine`, which refuses any number or
 *     name the facts do not support. A system prompt is a request; that is a
 *     check. See the header of `verify.ts` for why both exist.
 *   • Every failure — network, key, timeout, malformed, refused — returns the
 *     deterministic line. There is no path through this file that ends in a
 *     blank panel or a thrown error.
 *   • It is OFF unless the caller turns it on, and the caller finds out whether
 *     it is even available by asking, not by trying and stalling.
 *
 * WHY THE FACTS GO OVER THE WIRE AND THE ENGINE'S SENTENCE DOES NOT.
 * `worker/index.ts` tells the model to treat its input as inert data, which is
 * the right instruction. Sending the engine's own prose alongside it would hand
 * the model a paragraph to paraphrase and quietly turn a fact-grounded writer
 * into a rewriter — and the failure mode of a rewriter is that it keeps the
 * shape and drifts the content. It gets the facts and the angle. Nothing else.
 */

import type { BoothBeat } from '../domain/booth/booth.ts';
import { vocabularyFor, verifyLine, type RefusalReason } from '../domain/booth/verify.ts';
import {
  CAST,
  CAST_WORDS,
  castIds,
  castWords,
  DEFAULT_CAST,
  type Cast,
  fallbackCue,
  handoffDue,
  memberFor,
  type Handoff,
  type MemberId,
  type NarrateRole,
} from '../domain/booth/cast.ts';
import { bankExchange } from '../domain/booth/exchange.ts';
import type { StoryBible } from '../domain/booth/bible.ts';

export type { NarrateRole, MemberId } from '../domain/booth/cast.ts';

/**
 * Which voice suits which beat. The booth has already chosen the angle; this
 * only decides who says it, and it is derived from the director's own vocabulary
 * rather than from a second opinion about the moment.
 */
export function roleFor(beat: BoothBeat): NarrateRole {
  if (beat.kind === 'close') return 'feature';
  if (beat.kind === 'quiet') return 'colour';
  // 'consequence' is the aftermath of an incident — repairs, damage, the crew.
  // That is the pit reporter's beat, and it is the only angle that is.
  if (beat.angle === 'consequence') return 'pit';
  // 'season' and 'rivalry' are both arguments about why it matters, which is
  // colour by definition. Everything else is happening right now.
  if (beat.angle === 'season' || beat.angle === 'rivalry') return 'colour';
  return 'pbp';
}

export interface NarratedBeat extends BoothBeat {
  /** The line actually shown. Equal to `beat.line` when the model did not win. */
  line: string;
  /** True when a verified model line replaced the deterministic one. */
  aiWritten: boolean;
  /** Why a model line was not used. Null when one was, or when AI was off. */
  refusedBecause: RefusalReason | 'unavailable' | 'timeout' | null;
  refusalDetail: string | null;
  /** Which kind of line this is. */
  role: NarrateRole;
  /** WHO says it. Always set, with narration on or off. */
  speaker: MemberId;
  /** The throw that precedes it, or null. Rationed by `handoffDue`. */
  handoff: Handoff | null;
  /**
   * D-EZ-61 — THE REST OF THE EXCHANGE. Empty on a routine beat and with the
   * model off; one or two answering turns on a moment worth talking about.
   *
   * The first turn is `line` and is NOT repeated here — this is what came back
   * at him.
   */
  exchange: readonly SpokenLine[];
  /** Turns the gate threw out of an exchange. Zero unless one was refused. */
  exchangeRefused: number;
  /**
   * WHY the exchange stopped, when it did — D-EZ-78.
   *
   * Howard, 21 Aug 2026: *"what does that mean that its stopping the exchanges?
   * If there are natural exchanges set to happen why would we stop it?"*
   *
   * The count on its own cannot answer that. "The model tried to say Talladega
   * and nobody gave it Talladega" is the gate doing its job; "the line was 712
   * characters" is a brief that wants shortening. Both printed the same
   * sentence, so the difference was invisible from the outside — including to
   * us, which is the part that mattered.
   *
   * The lead line has carried `refusedBecause`/`refusalDetail` since D-EZ-61.
   * This is the same receipt for the turns that answer it.
   */
  exchangeRefusedBecause: RefusalReason | null;
  exchangeRefusalDetail: string | null;
}

/**
 * ⚠ FOUR SECONDS WAS THE REAL CEILING ON THE COMMENTARY, AND NOBODY HAD LOOKED.
 *
 * Howard, 19 Aug 2026, asked for richer commentary. The prompts had already been
 * widened once and the token budget raised once; what had never moved was this
 * number. Luna is a REASONING model — it thinks before it writes, the thinking
 * is billed and timed like everything else, and a four-second abort lands
 * squarely in the middle of that. Every one of those aborts is scored as a
 * failure and replaced with the engine's flat sentence, so the symptom is
 * exactly "the commentary is a bit thin" and the cause is a stopwatch.
 *
 * It is safe to be generous here and it always was: narration runs in the
 * background while the deterministic call is already on screen (see
 * `BoothCall.tsx`), so a slow beat delays a REWRITE, not the broadcast.
 *
 * A cue stays tight. It is eight words with no reasoning behind it, and one that
 * takes eight seconds is one that has gone wrong.
 */
const CUE_TIMEOUT_MS = 10_000;
const BEAT_TIMEOUT_MS = 45_000;
const BIG_TIMEOUT_MS = 120_000;

/**
 * One line of the broadcast, with the person who said it attached.
 *
 * ⚠ THE BOOTH USED TO SEND BARE STRINGS, LABELLED `avoidRepeating`, AND NOTHING
 * ELSE. That is enough to stop a voice repeating itself and not nearly enough to
 * make three voices sound like they are in the same room. Ned answered a throw
 * without being told what Marty had just described; Reba reported on an incident
 * without knowing whether it had already been called. Each voice was writing
 * into a vacuum and the result reads as three people narrating in turn — which
 * is exactly the thing the cast was built to stop being.
 *
 * So the transcript now travels ATTRIBUTED, and the worker uses it twice: once
 * as the conversation to build on, and once as the list not to repeat. Those are
 * different jobs and they were being done by one unlabelled array of strings.
 */
export interface SpokenLine {
  who: MemberId;
  line: string;
}

/**
 * D-003's tiering threshold. Defined in the DIRECTOR now, beside the scale it
 * is measured against, because `booth.ts` needs it for §6's expanding beat and
 * a domain module must not reach up into `src/lib` for it. Re-exported here so
 * every caller that already imported it from this file keeps working.
 */
export { BIG_MOMENT_SCORE } from '../domain/booth/director.ts';
import { BIG_MOMENT_SCORE } from '../domain/booth/director.ts';
/**
 * WHOSE KEY IS PAYING — D-EZ-48.
 *
 * `aiHeaders()` is `{}` when nobody has brought a key, which is the ordinary
 * case for Howard on the deployed Worker: no header, and the Worker spends its
 * own. A player who has locked one in sends it on every call below.
 */
import { aiAccess, aiSetupVersion, isNoModel } from './aiKey.ts';
/**
 * WHERE THEY ARE — D-EZ-52. The booth was blindfolded until this existed; see
 * the header of `domain/booth/setting.ts` for why that, and not the token
 * budget, was what made the commentary feel short on ambience.
 */
import { settingNumbers, settingWords, type BoothSetting } from '../domain/booth/setting.ts';
import { whyWords } from '../domain/booth/why.ts';

/** The close of a race is always a big moment, whatever its turn scored. */
export function isBigMoment(beat: BoothBeat): boolean {
  return beat.kind === 'close' || beat.score >= BIG_MOMENT_SCORE;
}

/** How many attributed lines of history each voice is given. */
const CONVERSATION_DEPTH = 6;

/**
 * WHO COMES BACK AT WHOM — D-EZ-61.
 *
 * Not a rotation and not random. It is the order a real booth answers in: the
 * call goes to the analyst because the question after "what happened" is "why",
 * and the analyst goes to pit road because the question after "why" is "what can
 * you see from down there". Ned answers Reba before Marty because a detail from
 * the garage is worth more after an argument than before one.
 */
const ANSWER_ORDER: Record<MemberId, readonly MemberId[]> = {
  host: ['analyst', 'pit'],
  analyst: ['pit', 'host'],
  pit: ['analyst', 'host'],
};

/**
 * How many voices answer this beat.
 *
 * ⚠ PROPORTIONAL, AND THAT IS EDITORIAL RATHER THAN THRIFTY. Howard's key is
 * paying and he has said not to nerf it, so this is not a budget: it is that
 * three people discussing a car holding station is worse writing than one person
 * mentioning it. A booth that convenes a panel for every beat has no way left to
 * signal that something mattered.
 *
 * The close of a race and the moments D-003 named get the full room. A beat that
 * scored well without clearing that bar, and that has enough facts for a second
 * person to have something to say, gets one answer. Everything else is one voice,
 * exactly as before.
 */
export function followUpsFor(beat: BoothBeat): number {
  if (beat.broadcast) return 0; // The new broadcast already gives each seat a distinct, sourced contribution.
  if (isBigMoment(beat)) return 2;
  if (beat.score >= BIG_MOMENT_SCORE / 2 && beat.facts.length >= 2) return 1;
  return 0;
}

/**
 * The same question, asked of a beat that has NEIGHBOURS — D-EZ-74.
 *
 * ⚠ §6's AFTERMATH IS ALREADY THE SECOND VOICE. A big turn expands into "the
 * moment, the aftermath, the pit reporter", and the aftermath is built from
 * exactly the facts the moment had no room for. Hang an exchange off the moment
 * as well and both draw from the same leftover pile, so the wreck gets narrated
 * three times running:
 *
 *     Marty:  Lonnie Ackerd 5 spots to the good, Hank Ackerd 5 spots lost…
 *     Reba:   They've found each other once already, and at some point…
 *     Ned:    Down here it is a scrapyard — they've found each other once already.
 *
 * Found by driving a real race in the browser and reading the rail. The rule is
 * that a turn gets ONE structure: either the moment answers itself in an
 * exchange, or it expands into an aftermath beat with its own speaker. Never
 * both, because both is the same fact three times with three name badges on it.
 */
export function followUpsAt(beats: readonly BoothBeat[], index: number): number {
  const beat = beats[index];
  if (!beat) return 0;
  const next = beats[index + 1];
  if (next && next.kind === 'aftermath' && next.turn === beat.turn) return 0;
  return followUpsFor(beat);
}

/** The colleagues who answer, in speaking order. */
export function respondersFor(speaker: MemberId, count: number): MemberId[] {
  return ANSWER_ORDER[speaker].slice(0, Math.max(0, count));
}

/**
 * Is narration configured at all?
 *
 * Asked once and cached. Without this the interface would discover the missing
 * key by waiting for a timeout on every beat of every race, which is the same
 * thing as the feature being broken.
 */
let availability: Promise<boolean> | null = null;
/**
 * WHICH KEY THE CACHED ANSWER WAS ABOUT.
 *
 * The probe is cached for the sitting, and it has to be — without it the app
 * would discover a missing key by waiting for a timeout on every beat of every
 * race. But "is narration available" changes the instant a player locks a key
 * in, and a cache that outlived that would tell a paying player the booth was
 * switched off. The version counter in `aiKey.ts` is what makes this cheap.
 */
let availabilityFor = -1;

export function narrationAvailable(): Promise<boolean> {
  if (availability !== null && availabilityFor === aiSetupVersion()) return availability;
  availabilityFor = aiSetupVersion();
  // `facts.probe` is answered by the worker without touching the upstream model
  // — it is a question about configuration, not about racing, and it used to
  // buy a completion to answer it on every page load.
  // D-EZ-100 — a visitor with no key and no owner session is not asked about
  // at all: there is nothing the Worker could say but "no model".
  availability = aiAccess()
    .then(async (access) => {
      if (access.payer === 'none') return false;
      const r = await fetch('/api/narrate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...access.headers },
        body: JSON.stringify({ facts: { probe: true }, role: 'pbp' }),
      });
      if (!r.ok) return false;
      const body = (await r.json().catch(() => null)) as { ok?: unknown } | null;
      return body?.ok === true && !isNoModel(body);
    })
    .catch(() => false);
  return availability;
}

/** Test seam — forget the cached probe. */
export function resetNarrationAvailability(): void {
  availability = null;
  availabilityFor = -1;
}

/**
 * Narrate one beat, or hand back exactly what came in.
 *
 * `names` is every driver the beat is entitled to mention — normally the
 * subject and object the rules named. It is the caller's job because only the
 * caller can resolve a driver id to the name on the card.
 */
export async function narrateBeat(
  beat: BoothBeat,
  names: readonly string[],
  spokenSoFar: readonly SpokenLine[] = [],
  handoff: Handoff | null = null,
  setting: BoothSetting | null = null,
  /** Abandon the call — the player switched the booth off or left the race. */
  signal?: AbortSignal,
  /**
   * How many colleagues answer, when the caller knows something this beat does
   * not — see `followUpsAt`. Omitted, the beat decides for itself exactly as
   * it always has.
   */
  followUps?: number,
  /**
   * D-EZ-111 — the league's booth and today's story bible. The cast travels as
   * pool ids (the Worker owns the voices); the bible's facts join the gate's
   * vocabulary, so a callback the bible supports passes and nothing else does.
   */
  league: { cast?: Cast; bible?: StoryBible } = {},
): Promise<NarratedBeat> {
  const cast = league.cast ?? DEFAULT_CAST;
  const role = roleFor(beat);
  const speaker = memberFor(role).id;
  /**
   * WHAT THE WRITER IS ACTUALLY SHOWN — computed once, used for the payload AND
   * for the gate, so the two can never disagree about what was on offer.
   */
  const sentFacts = beat.moreFacts.length > 0 ? beat.moreFacts : beat.facts;
  const keep = (
    reason: NarratedBeat['refusedBecause'],
    detail: string | null,
  ): NarratedBeat => ({
    ...beat,
    aiWritten: false,
    refusedBecause: reason,
    refusalDetail: detail,
    role,
    speaker,
    handoff,
    exchange: beat.broadcast?.voices ?? [],
    exchangeRefused: 0,
    exchangeRefusedBecause: null,
    exchangeRefusalDetail: null,
  });

  // A beat the booth itself fell back on has facts too thin to be worth a
  // model call — asking for prose about nothing is how invention starts.
  if (beat.facts.length === 0) return keep('unavailable', 'no facts to write from');

  const wanted = followUps ?? followUpsFor(beat);
  const responders = respondersFor(speaker, wanted);
  const big = isBigMoment(beat);

  // D-EZ-100 — no key and not the owner: no request at all, the written line stands.
  const access = await aiAccess();
  if (access.payer === 'none') return keep('unavailable', 'no model for this visitor');

  const controller = new AbortController();
  const budget = big ? BIG_TIMEOUT_MS : BEAT_TIMEOUT_MS;
  const timer = setTimeout(() => controller.abort(), budget);
  /*
    ⚠ AND THE CALLER CAN STOP IT — which matters far more now than it did.

    With a four-second abort, a call abandoned by a player leaving the race cost
    four seconds of somebody's money. At two minutes on a big moment, with the
    player's own key on it, an abandoned race that keeps writing to the end is a
    bill for a broadcast nobody is watching.
  */
  const stop = () => controller.abort();
  signal?.addEventListener('abort', stop, { once: true });
  if (signal?.aborted) controller.abort();

  let payload: { line?: unknown; lines?: unknown };
  try {
    const res = await fetch('/api/narrate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...access.headers },
      signal: controller.signal,
      body: JSON.stringify({
        role,
        // The id only. The brief that shapes this voice lives in the worker —
        // see the header of `cast.ts` for why a voice string must never travel
        // from the browser into a system prompt.
        speaker,
        // Send the strong model for the moments D-003 named.
        big,
        /*
          D-EZ-61 — ASK FOR THE WHOLE EXCHANGE IN ONE CALL.

          Not two calls with the transcript passed between them. That was the
          old shape and it is why the booth read as three people narrating in
          turn: written separately, the best the second voice can do is not
          repeat the first. Written together, it can pick up his last clause and
          argue with it.
        */
        followUps: wanted,
        responders,
        // THE CONVERSATION, attributed. This is what makes three voices
        // coordinate rather than take turns: whoever is speaking can see who
        // said what, and can answer it, build on it, or disagree with it.
        conversation: spokenSoFar.slice(-CONVERSATION_DEPTH).map((l) => ({
          who: cast[l.who].short,
          line: l.line,
        })),
        // What was just said TO this person, together with the line it was
        // said ABOUT — a throw with no context behind it is a question about
        // nothing, which is how a "coordinated" booth ends up answering itself.
        respondingTo: handoff
          ? {
              from: cast[handoff.from].short,
              cue: handoff.cue,
              about: spokenSoFar[spokenSoFar.length - 1]?.line ?? null,
            }
          : undefined,
        facts: {
          turn: beat.turn,
          angle: beat.angle,
          // Facts only. Not the engine's sentence — see the header.
          //
          // D-EZ-74 — SIX RATHER THAN THREE. `MAX_FACTS` is three because
          // `factClause` CONCATENATES, and four true clauses in one breath is a
          // recitation. A model SELECTS. Holding it to a limit that exists to
          // protect the other code path was throwing away the best material in
          // the ledger for no reason. See `researcher.MODEL_FACTS`.
          facts: sentFacts.map((f) => ({ kind: f.kind, text: f.text, value: f.value })),
          drivers: names,
          story: beat.broadcast ? { headline: beat.broadcast.headline, call: beat.line, analysis: beat.broadcast.voices, nextStakes: beat.broadcast.stakes } : undefined,
        },
        /*
          WHY IT HAPPENED — D-EZ-74, and the reason the analyst's seat had
          nothing to do.

          Facts are OUTCOMES. Setting is a PLACE. Neither carries the mechanism,
          so Reba Vance — hired to answer the question after "what happened" —
          could only restate the outcome Marty had just given. Two voices saying
          one thing is what "add another voice" degenerates into when the second
          knows nothing the first did not.

          ⚠ MECHANISM, NEVER ARITHMETIC. No die, no chart, no citation crosses
          this line; `why.ts` translates the engine's own `momentTag` into one
          sentence of racing English and refuses rather than guesses. Howard
          ruled on that shape at D-EZ-69 — tell me what happened "without adding
          chart references etc."
        */
        why: beat.why ?? undefined,
        /*
          THE SETTING — D-EZ-52, and the whole of the ambience fix.

          Sent SEPARATELY from `facts` and not folded into it, because the two
          are governed by different rules on the other side: a fact is a thing
          that happened and may be reported, and the setting is a place that
          exists and may be described. The system prompt draws exactly that line,
          and it can only draw it because the payload does.

          Null when the caller has no race to hand — an exhibition beat replayed
          out of context — and the booth then behaves precisely as it did before
          any of this, which is the honest fallback.
        */
        setting: setting ?? undefined,
        /*
          D-EZ-111 — WHO IS IN THE BOOTH, AND WHAT THEY KNOW TODAY. Pool ids
          only for the cast (the Worker builds the voices from its own table),
          and the story bible as inert data: storylines, the analyst's pick,
          the rivalries in this field, the league history that matters. Every
          name and number in it is also in the gate's vocabulary above.
        */
        cast: league.bible?.cast ?? castIds(cast),
        bible: league.bible
          ? {
              booth: league.bible.booth,
              analystPast: league.bible.analystPast,
              pick: league.bible.pick,
              storylines: league.bible.storylines,
              rivalries: league.bible.rivalries,
              history: league.bible.history,
            }
          : undefined,
      }),
    });
    if (!res.ok) return keep('unavailable', `worker said ${res.status}`);
    payload = (await res.json()) as { line?: unknown };
  } catch (e) {
    return keep(
      (e as Error)?.name === 'AbortError' ? 'timeout' : 'unavailable',
      (e as Error)?.name === 'AbortError' ? `no answer in ${budget}ms` : 'network',
    );
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', stop);
  }

  if (typeof payload.line !== 'string') return keep('unavailable', 'no line in the response');

  // THE GATE. Everything above this point is plumbing; this is the rule.
  /*
    ⚠ THE GATE IS BUILT FROM WHAT WAS ACTUALLY SENT — D-EZ-74.

    `vocabularyFor`'s own rule is *"a model that was only shown three facts must
    not be allowed to be right by accident about a fourth."* The corollary is
    that once it IS shown six, the gate has to know about six, or every line
    that uses the fourth is refused as a fabrication and the booth gets worse
    the moment it is given more to work with.

    Same for the mechanism (`why.ts`): handing it the words "pace car" and
    "yellow" and then refusing them is the failure D-EZ-52 already hit with the
    setting. The gate has not been loosened. It has been told what else is true.
  */
  const vocab = vocabularyFor([...sentFacts, ...(league.bible?.facts ?? [])], names, {
    // ⚠ WITHOUT THESE TWO LINES THE FEATURE MAKES THE BOOTH WORSE.
    //
    // The gate refuses any capitalised word that is not a known name and any
    // number no fact supports — that is what stops an invented driver and an
    // invented lap count. Give the booth a racetrack and let it say "Bristol"
    // or "half a mile" and every one of those correct lines is thrown out as a
    // fabrication, and the beat falls back to the engine's flat sentence.
    //
    // A word is sayable because it is TRUE and was handed over. The gate has not
    // been loosened; it has been told what else is true.
    alsoAllowNumbers: setting ? settingNumbers(setting) : undefined,
    // A superlative is allowed only where the booth had already earned one —
    // the model never gets to be the first to claim "the biggest of the day".
    allowSuperlative: /\b(biggest|worst|best|fastest|first)\b/i.test(beat.line),
    // The three people in the booth may address each other by name. They are
    // not facts about the race, so they are not in the facts, so without this
    // the gate refuses every answer to a handoff.
    alsoAllow: [
      ...CAST_WORDS,
      ...castWords(cast),
      ...(setting ? settingWords(setting) : []),
      ...(beat.why ? whyWords(beat.why) : []),
    ],
    // AND NOW IT IS A CHECK. The transcript used to go to the model as a
    // request not to repeat itself and was never verified. Repetition is the
    // failure mode §8 calls the one that matters most, and it was the only rule
    // in the booth enforced by asking politely.
    recent: spokenSoFar.map((l) => l.line),
  });
  const verdict = verifyLine(payload.line, vocab);
  if (!verdict.ok) return keep(verdict.reason, verdict.detail);

  /*
    ⚠ EVERY TURN OF THE EXCHANGE GOES THROUGH THE SAME GATE — D-EZ-61.

    Three speakers is three chances to invent a driver or a lap count, not one,
    and the second and third turns are the riskier ones: they are written to
    ANSWER something, and answering is where a model reaches for a detail nobody
    gave it. The rule does not soften because there are more voices.

    ⚠ AND A REFUSED TURN ENDS THE EXCHANGE RATHER THAN BEING DROPPED OUT OF THE
    MIDDLE OF IT. Keeping turn 3 after refusing turn 2 would print an answer to
    something the player never saw — a conversation with a hole in it reads as
    worse writing than a short one, and stitching over the hole would be
    repairing a failed generation, which this project does not do anywhere else.
    So the exchange is truncated where it stopped being verifiable, and how many
    turns that cost is carried out on the beat rather than swallowed.

    The FIRST turn is the beat itself and is not optional: it has already been
    through the gate above, and if it failed, none of this ran.
  */
  const raw = Array.isArray(payload.lines) ? (payload.lines as { who?: unknown; line?: unknown }[]) : [];
  const exchange: SpokenLine[] = [];
  let exchangeRefused = 0;
  let exchangeRefusedBecause: RefusalReason | null = null;
  let exchangeRefusalDetail: string | null = null;
  const said: string[] = [...spokenSoFar.map((l) => l.line), payload.line];
  for (const turn of raw.slice(1)) {
    const who = String(turn?.who ?? '');
    const text = String(turn?.line ?? '');
    if (!(who in CAST) || text.trim().length === 0) {
      exchangeRefused = raw.length - 1 - exchange.length;
      exchangeRefusedBecause = 'empty';
      exchangeRefusalDetail = who in CAST
        ? 'the answer came back blank'
        : `no such person in the booth as "${who.slice(0, 24)}"`;
      break;
    }
    // Rebuilt per turn so each one is measured against everything said before
    // it, including the turns of this same exchange. Two colleagues agreeing in
    // the same words is the repetition §8 calls the one that matters most.
    const turnVocab = { ...vocab, recent: said };
    const turnVerdict = verifyLine(text, turnVocab);
    if (!turnVerdict.ok) {
      exchangeRefused = raw.length - 1 - exchange.length;
      // The FIRST refusal is the one that ended the conversation; the turns
      // after it were never judged and have no reason to report.
      exchangeRefusedBecause = turnVerdict.reason;
      exchangeRefusalDetail = turnVerdict.detail;
      break;
    }
    exchange.push({ who: who as MemberId, line: text.trim() });
    said.push(text);
  }

  return {
    ...beat,
    line: payload.line.trim(),
    aiWritten: true,
    refusedBecause: null,
    refusalDetail: null,
    role,
    speaker,
    handoff,
    exchange: beat.broadcast?.voices ?? exchange,
    exchangeRefused,
    exchangeRefusedBecause,
    exchangeRefusalDetail,
  };
}

/**
 * Ask the outgoing voice to throw to the incoming one.
 *
 * A separate, much tighter call than a beat: eight words, no facts, no numbers,
 * ending on a name. It is allowed to fail freely — `fallbackCue` is a perfectly
 * good throw and the deterministic booth has to work with the model switched
 * off anyway, so this is the one place in the file where the fallback is not a
 * consolation prize.
 */
async function writeCue(
  from: MemberId,
  to: MemberId,
  beat: BoothBeat,
  recentCues: readonly string[],
  signal?: AbortSignal,
): Promise<string> {
  const plain = fallbackCue(CAST[to], beat);
  const access = await aiAccess();
  if (access.payer === 'none') return plain;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), CUE_TIMEOUT_MS);
  const stop = () => controller.abort();
  signal?.addEventListener('abort', stop, { once: true });
  if (signal?.aborted) return plain;
  try {
    const res = await fetch('/api/narrate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...access.headers },
      signal: controller.signal,
      body: JSON.stringify({
        role: 'cue',
        speaker: from,
        throwTo: to,
        facts: { angle: beat.angle, turn: beat.turn },
        recentLines: recentCues.slice(-6),
      }),
    });
    if (!res.ok) return plain;
    const payload = (await res.json()) as { line?: unknown };
    if (typeof payload.line !== 'string') return plain;

    // A throw carries no facts, so it is entitled to NO numbers and to nothing
    // but the cast's own names. That is a stricter gate than a beat gets, and
    // it is stricter on purpose: there is nothing here for a number to be true
    // about, so any number in it is invented by definition.
    const verdict = verifyLine(payload.line, {
      numbers: new Set<string>(),
      names: new Set(CAST_WORDS),
      allowSuperlative: false,
      // A throw is six words long, so an echo of an earlier throw is the most
      // noticeable repetition in the whole broadcast.
      recent: recentCues,
    });
    if (!verdict.ok) return plain;
    const cue = payload.line.trim();
    // It has to actually address the person, or it is not a throw.
    return cue.includes(CAST[to].short) ? cue : plain;
  } catch {
    return plain;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', stop);
  }
}

/**
 * Narrate a whole call, in order, never more than a few at a time.
 *
 * Sequential rather than parallel on purpose: `recentLines` is what stops the
 * model repeating itself, and a parallel fan-out would hand every request the
 * same empty history and get back six variations of one sentence. The handoffs
 * make that doubly true — a throw only makes sense if you know who spoke last,
 * which is a fact that does not exist until the previous beat is finished.
 */
export async function narrateCall(
  beats: readonly BoothBeat[],
  namesFor: (beat: BoothBeat) => readonly string[],
  /** Where the race is, per beat. Omitted, the booth calls it blindfolded. */
  settingFor?: (beat: BoothBeat) => BoothSetting | null,
  /**
   * D-EZ-61 — HANDED OVER AS EACH ONE LANDS, rather than all of them at the end.
   *
   * The screen used to wait for the whole call before showing any of it, which
   * was defensible when a beat took a second or two. It is not defensible now: a
   * twenty-beat race where the big moments think hard is minutes of work, and
   * holding every rewritten line back until the last one arrives means a player
   * watches the entire race on the deterministic floor and then gets the good
   * version after they have stopped looking.
   *
   * Publishing per beat costs nothing — they are written in order anyway — and
   * turns a long wait into the booth catching up with the race, which is what a
   * real one does.
   */
  options: {
    onBeat?: (index: number, beat: NarratedBeat) => void;
    /** Stop writing. See `narrateBeat` — an abandoned race must stop spending. */
    signal?: AbortSignal;
  } = {},
): Promise<NarratedBeat[]> {
  const out: NarratedBeat[] = [];
  const spoken: SpokenLine[] = [];
  const cues: string[] = [];
  let previousSpeaker: MemberId | null = null;
  let sinceHandoff = Number.POSITIVE_INFINITY;

  for (let i = 0; i < beats.length; i++) {
    const beat = beats[i];
    // Checked at the top of every beat as well as inside the fetch: a loop that
    // only notices an abort when a request comes back still writes one more.
    if (options.signal?.aborted) break;
    const incoming = memberFor(roleFor(beat)).id;

    let handoff: Handoff | null = null;
    if (handoffDue(beat, incoming, previousSpeaker, sinceHandoff)) {
      const from = previousSpeaker as MemberId;
      handoff = { from, to: incoming, cue: await writeCue(from, incoming, beat, cues, options.signal) };
      cues.push(handoff.cue);
      sinceHandoff = 0;
    } else {
      sinceHandoff += 1;
    }

    const done = await narrateBeat(
      beat,
      namesFor(beat),
      spoken,
      handoff,
      settingFor?.(beat) ?? null,
      options.signal,
      // D-EZ-74 — a moment that expands into an aftermath does not also
      // convene a panel. See `followUpsAt`.
      followUpsAt(beats, i),
    );
    if (options.signal?.aborted) break;
    options.onBeat?.(out.length, done);
    out.push(done);
    /*
      THE WHOLE EXCHANGE GOES INTO THE TRANSCRIPT, not just the beat's own line.
      The next voice has to be able to see that Ned already answered this one
      from pit road, or it will answer it again from upstairs.
    */
    spoken.push({ who: done.speaker, line: done.line });
    for (const turn of done.exchange) spoken.push(turn);
    previousSpeaker = done.exchange.length > 0
      ? done.exchange[done.exchange.length - 1].who
      : done.speaker;
  }
  return out;
}

/**
 * The same walk, with no network in it at all.
 *
 * The cast is not an AI feature. With narration off every beat still has a
 * speaker, a seat and a badge, and the throws still happen — they just use
 * `fallbackCue`. This is what the interface calls when `/api/narrate` does not
 * answer, and it is why turning the model off changes the polish rather than
 * the format.
 */
export function castCall(beats: readonly BoothBeat[]): NarratedBeat[] {
  const out: NarratedBeat[] = [];
  let previousSpeaker: MemberId | null = null;
  let sinceHandoff = Number.POSITIVE_INFINITY;

  for (let i = 0; i < beats.length; i++) {
    const beat = beats[i];
    const role = roleFor(beat);
    const speaker = memberFor(role).id;
    let handoff: Handoff | null = null;
    if (!beat.broadcast && handoffDue(beat, speaker, previousSpeaker, sinceHandoff)) {
      handoff = { from: previousSpeaker as MemberId, to: speaker, cue: fallbackCue(CAST[speaker], beat) };
      sinceHandoff = 0;
    } else {
      sinceHandoff += 1;
    }
    /*
      D-EZ-74 — THE FLOOR TALKS BACK.

      This used to be `exchange: []`, with the note that *"three people arguing
      is the one thing a template cannot fake, so with the model off a beat is
      one voice and says so by being one voice."*

      That is true of an ARGUMENT and false of the far more common thing a booth
      actually does, which is: one person says what happened, and the next picks
      up a DIFFERENT TRUE FACT FROM THE SAME MOMENT and says what it means from
      where they sit. `exchange.ts` builds exactly that, out of facts the beat is
      already carrying — so D-003 holds, and a beat with nothing left to add
      still produces no exchange rather than a manufactured one.

      Same rationing as the model path: `followUpsFor` decides how many voices,
      `respondersFor` decides which, so turning the key on changes who WROTE the
      answers and not whether there are any.
    */
    const bank = beat.broadcast?.voices ?? bankExchange(beat, respondersFor(speaker, followUpsAt(beats, i)));

    out.push({
      ...beat,
      aiWritten: false,
      refusedBecause: null,
      refusalDetail: null,
      role,
      speaker,
      handoff,
      exchange: bank,
      exchangeRefused: 0,
      exchangeRefusedBecause: null,
      exchangeRefusalDetail: null,
    });
    previousSpeaker = bank.length > 0 ? bank[bank.length - 1].who : speaker;
  }
  return out;
}
